import React, { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUndo, faCheck, faTimes, faLock, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { dataModules } from '../db/data';
import { collectPriceFields } from '../utils/priceOverrides';
import {
  savePriceOverridesApi,
  loginApi,
  logoutApi,
  checkAuthApi,
} from '../utils/priceOverridesApi';
import { usePriceOverrides } from '../context/PriceOverridesContext';
import './AdminPage.css';

const LoginForm = ({ onLoggedIn }) => {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await loginApi(password);
      setPassword('');
      onLoggedIn();
    } catch (err) {
      setError(err.code === 'INVALID_PASSWORD' ? 'Incorrect password.' : (err.message || 'Login failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-login-container">
        <form className="admin-login-card" onSubmit={handleSubmit}>
          <div className="admin-login-icon">
            <FontAwesomeIcon icon={faLock} />
          </div>
          <h1>Admin Login</h1>
          <p className="admin-login-subtitle">Sign in to edit menu prices.</p>
          <input
            type="password"
            className="admin-login-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            disabled={submitting}
            autoComplete="current-password"
          />
          {error && <div className="admin-login-error">{error}</div>}
          <button type="submit" className="btn-save" disabled={submitting || !password}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { overrides: storedOverrides, loading, error, refresh } = usePriceOverrides();
  const [overrides, setOverrides] = useState(storedOverrides);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [authState, setAuthState] = useState('checking'); // 'checking' | 'anon' | 'authed'

  useEffect(() => {
    let cancelled = false;
    checkAuthApi().then((authed) => {
      if (cancelled) return;
      setAuthState(authed ? 'authed' : 'anon');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setOverrides(storedOverrides);
  }, [storedOverrides]);

  const priceFields = useMemo(() => {
    return Object.entries(dataModules).flatMap(([moduleKey, moduleData]) =>
      collectPriceFields(moduleKey, moduleData, overrides)
    );
  }, [overrides]);

  const handlePriceChange = (path, value) => {
    const newOverrides = { ...overrides };
    const field = priceFields.find((item) => item.path === path);
    if (field && value === field.currentValue) {
      delete newOverrides[path];
    } else {
      newOverrides[path] = value;
    }
    setOverrides(newOverrides);
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await savePriceOverridesApi(overrides);
      setSaveStatus('success');
      setHasChanges(false);
      refresh();
      setTimeout(() => setSaveStatus(null), 1500);
    } catch (saveError) {
      if (saveError.code === 'UNAUTHORIZED') {
        setAuthState('anon');
        setSaveStatus(null);
        return;
      }
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all prices to original values? This cannot be undone.')) {
      setSaveStatus('saving');
      try {
        await savePriceOverridesApi({});
        setOverrides({});
        setHasChanges(false);
        setSaveStatus('success');
        refresh();
        setTimeout(() => setSaveStatus(null), 1500);
      } catch (saveError) {
        if (saveError.code === 'UNAUTHORIZED') {
          setAuthState('anon');
          setSaveStatus(null);
          return;
        }
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }
  };

  const handleLogout = async () => {
    await logoutApi();
    setAuthState('anon');
    setOverrides(storedOverrides);
    setHasChanges(false);
  };

  const getDisplayValue = (field) =>
    overrides[field.path] !== undefined ? overrides[field.path] : field.currentValue;

  const isModified = (field) =>
    overrides[field.path] !== undefined && overrides[field.path] !== field.currentValue;

  const groupedFields = priceFields.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {});

  if (authState === 'checking') {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="price-category">
            <h2 className="category-title">Loading…</h2>
          </div>
        </div>
      </div>
    );
  }

  if (authState === 'anon') {
    return <LoginForm onLoggedIn={() => setAuthState('authed')} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <header className="admin-header">
          <div className="admin-header-row">
            <div>
              <h1>Menu Price Admin Console</h1>
              <p className="admin-subtitle">
                Edit prices for any item. Changes are saved to the master override list.
              </p>
            </div>
            <button type="button" className="btn-logout" onClick={handleLogout} title="Sign out">
              <FontAwesomeIcon icon={faSignOutAlt} /> Sign out
            </button>
          </div>
        </header>

        <div className="admin-actions">
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={!hasChanges || saveStatus === 'saving' || loading}
          >
            <FontAwesomeIcon icon={faSave} />
            {saveStatus === 'saving' ? ' Saving...' : ' Save All Changes'}
          </button>

          <button
            type="button"
            className="btn-reset"
            onClick={handleReset}
            disabled={saveStatus === 'saving' || loading}
          >
            <FontAwesomeIcon icon={faUndo} />
            Reset All to Original
          </button>

          {saveStatus === 'success' && (
            <span className="save-status success">
              <FontAwesomeIcon icon={faCheck} />
              Changes saved successfully!
            </span>
          )}

          {saveStatus === 'error' && (
            <span className="save-status error">
              <FontAwesomeIcon icon={faTimes} />
              Error saving changes. Please try again.
            </span>
          )}
        </div>

        {loading && (
          <div className="price-category">
            <h2 className="category-title">Loading prices...</h2>
          </div>
        )}

        {error && !loading && (
          <div className="price-category">
            <h2 className="category-title">Unable to load prices</h2>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && Object.keys(groupedFields).map((category) => (
          <div key={category} className="price-category">
            <h2 className="category-title">{category}</h2>
            <div className="price-fields-grid">
              {groupedFields[category].map((field) => {
                const displayValue = getDisplayValue(field);
                const modified = isModified(field);

                return (
                  <div key={field.id} className={`price-field ${modified ? 'modified' : ''}`}>
                    <label htmlFor={field.id} className="price-label">
                      {field.label}
                      {modified && <span className="modified-badge">Modified</span>}
                    </label>
                    <div className="price-input-group">
                      <span className="currency-symbol">$</span>
                      <input
                        type="text"
                        id={field.id}
                        className="price-input"
                        value={displayValue}
                        onChange={(e) => handlePriceChange(field.path, e.target.value)}
                        placeholder={field.currentValue || '0.00'}
                        disabled={loading}
                      />
                    </div>
                    {field.currentValue && (
                      <div className="original-value">
                        Original: ${field.currentValue}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {hasChanges && (
          <div className="unsaved-changes-notice">
            <FontAwesomeIcon icon={faSave} />
            You have unsaved changes. Don't forget to save!
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
